import { useEffect, useRef, useState } from 'react';
import { BREAKDOWN_PANEL_CATEGORIES, ELEMENT_CATEGORIES, type ElementCategory } from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';

/** Where a tag lands: this scene, or every scene whose text names it (owner review 2026-10-27, D2). */
export type TagScope = 'SCENE' | 'EVERY_SCENE';

/**
 * TAG THE WORDS THE PRODUCER SELECTED — where a correction the AI missed begins.
 *
 * Anchored under the selection (measured against the reader), so the screenplay never moves
 * under the cursor. The categories are the sheet's own, in its order and with its colours, each
 * named; "this scene" is the default every time it opens, and "every scene it appears in" asks
 * again with the list before anything is written.
 */
export function TagPopover({
  text,
  top,
  left,
  disabledReason,
  onTag,
  onClose,
}: {
  text: string;
  top: number;
  left: number;
  /** Why tagging is not open to this person, or null. */
  disabledReason: string | null;
  onTag: (category: ElementCategory, scope: TagScope) => void;
  onClose: () => void;
}) {
  const [scope, setScope] = useState<TagScope>('SCENE');
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    root.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus({ preventScroll: true });
  }, []);

  return (
    <div
      ref={root}
      className="sr-popover sr-tagger"
      role="dialog"
      aria-label={`Tag “${text}” as`}
      data-testid="sr-tagger"
      style={{ top, left }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="sr-popover-head">
        <span className="sr-tagger-quote">&ldquo;{text}&rdquo;</span>
        <span className="sr-popover-sub">tag as</span>
        <button type="button" className="sr-popover-close" aria-label="Cancel tagging" data-testid="sr-tagger-close" onClick={onClose}>
          <Icon name="close" size={14} />
        </button>
      </div>

      {disabledReason !== null ? (
        <p className="sr-popover-note" data-testid="sr-tagger-refused">
          {disabledReason}
        </p>
      ) : (
        <>
          <div className="sr-tagger-scope" role="radiogroup" aria-label="Where to tag it" data-testid="sr-tag-scope">
            {(
              [
                ['SCENE', 'This scene', 'sr-tag-scope-scene'],
                ['EVERY_SCENE', 'Every scene it appears in', 'sr-tag-scope-every'],
              ] as const
            ).map(([value, label, testId]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={scope === value}
                className="ui-filter"
                aria-pressed={scope === value}
                data-testid={testId}
                data-autofocus={value === 'SCENE' ? true : undefined}
                onClick={() => {
                  setScope(value);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="sr-tagger-grid">
            {BREAKDOWN_PANEL_CATEGORIES.map((category) => {
              const meta = ELEMENT_CATEGORIES[category];

              return (
                <button
                  key={category}
                  type="button"
                  className="sr-tagger-cat"
                  title={meta.use}
                  data-testid={`sr-tag-${category}`}
                  onClick={() => {
                    onTag(category, scope);
                  }}
                >
                  <i aria-hidden="true" className="el-swatch" style={{ background: meta.colour }} />
                  {meta.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
